import React, { useCallback, useEffect, useRef, useState } from "react";

//Internal Imports
import DataGrid from "../../common/components/DataGrid/DataGrid";
import MenuLayout from "../../common/components/MenuLayout/MenuLayout";
import ConfirmationModal from "../../common/components/Modal/CorfimationModal";
import Modal from "../../common/components/Modal/Modal";
import Paint from "../../common/components/Paint/Paint";
import { COLORS } from "../../common/constants/colors";
import { getError, openNotification, tryCatch } from "../../common/constants/Utilities";
import { Mode } from "../../common/types/AllTypes";
import ViewContainer from "../../common/components/ViewContainer/ViewContainer";
import { useAuth } from "../../contexts/auth";
import { useListDepartmentByUer } from "../../hooks/useListDepartment";
import { IAttribute } from "../../interfaces/IAttribute";
import IColumn from "../../interfaces/IColumn";
import { IMenu } from "../../interfaces/IMenu";
import { DeactivateAttributeByID, GetAttributeByID, GetAttributesByUserID } from "../../services/attribute";
import AddUpdateAttribute from "./AddUpdateAttribute";
import './Atributos.css';
import { LoadingIcon } from "../../common/constants/cssElements";


// Interfaces
interface Props {
    menu: IMenu;
}

const Atributos: React.FC<Props> = ({ menu }) => {

    // States
    const { user } = useAuth();
    const { listDepartment, loadingDeparment } = useListDepartmentByUer();
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingAction, setLoadingAction] = useState<boolean>(false);
    const [listAttribute, setListAttribute] = useState<IAttribute[]>([]);
    const [deleteModal, setDeleteModal] = useState<boolean>(false);
    const [addUpdateModal, setAddUpdateModal] = useState<boolean>(false);
    const [attributeUpdate, setAttributeUpdate] = useState<IAttribute>();
    const [mode, setMode] = useState<Mode>('Add');
    const atrrDep = useRef<IAttribute>();
    const [searchV, setSearchV] = useState<string>('');
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const [loadingDetail, setLoadingDetail] = useState<boolean>(false);
    const [idDetail, setIdDetail] = useState<number | null>(null);


    // Consts
    const columns: IColumn[] = [
        {
            title: 'Nombre',
            dataIndex: 'nombre',
        },
        {
            title: 'Departamento',
            dataIndex: 'departamentoNombre',
        },
        {
            title: <i className="fa-solid fa-rotate button_refresh" onClick={() => { handleGetAttributesByUserID(); setSearchV('') }}></i>,
            dataIndex: '',
            render: (attr: IAttribute) => loadingDetail && idDetail === attr.atributoID ? <LoadingIcon color={COLORS.blue_active} size={'20px'} /> : <></>
        }
    ];


    // handles
    const handleGetAttributesByUserID = () => {
        tryCatch(() => {
            setLoading(true);
            GetAttributesByUserID(user!.usuarioID)
                .then((response => {
                    setListAttribute(response.data);
                }))
                .catch((response) => {
                    openNotification('error', 'Error', getError(response?.msg));
                })
                .finally(() => setLoading(false));
        })
    }

    const handleGetAttributeByID = (attribute: IAttribute) => {
        setAttributeUpdate(undefined);
        atrrDep.current = undefined;
        if (!!attribute) {
            setIdDetail(attribute.atributoID);
            setLoadingDetail(true);
            tryCatch(() => {
                GetAttributeByID(attribute.atributoID!)
                    .then(({ data }) => {
                        atrrDep.current = attribute;
                        setAttributeUpdate(data);
                    })
                    .catch((response) => {
                        openNotification('error', 'Error', getError(response?.msg));
                    })
                    .finally(() => setLoadingDetail(false))
            })
        }
    }


    const handleDeactiveAttribute = () => {
        tryCatch(() => {
            if (!!attributeUpdate) {
                setLoadingAction(true);
                DeactivateAttributeByID(attributeUpdate.atributoID!)
                    .then((response) => {
                        openNotification('success', `Atributo (${attributeUpdate.nombre}) eliminado`, response.msg!);
                        setListAttribute(listAttribute.filter(attr => attr.atributoID !== attributeUpdate.atributoID!));

                    })
                    .catch((response) => {
                        openNotification('error', 'Error', getError(response?.msg));
                    })
                    .finally(() => {
                        setLoadingAction(false);
                        setDeleteModal(false);
                        setAttributeUpdate(undefined);
                    })
            }
        })
    }

    // Methods
    const openModalUpdate = () => {
        if (!!!atrrDep.current?.departamentoNombre) {
            openNotification('warning', `Este Grupo tiene el Departamento Deshabilitado`, 'Contáctese con el equipo Bis para más información.');
        } else {
            setMode('Update');
            setAddUpdateModal(true);
        }
    }


    const getAttributesFiltered = useCallback((): IAttribute[] => {
        let attributesFiltered: IAttribute[] = [];
        const valueLowerCase = searchV.toLowerCase();

        if (!!valueLowerCase) {
            attributesFiltered = listAttribute.filter((attr) => attr.nombre.toLowerCase().includes(valueLowerCase) || attr.departamentoNombre!.toLowerCase().includes(valueLowerCase));
        } else {
            attributesFiltered = listAttribute;
        }

        return attributesFiltered;
    }, [searchV, listAttribute]);

    //Effects
    useEffect(() => {
        handleGetAttributesByUserID();
    }, []);

    return (
        <MenuLayout currentPage={menu.nombre}>
            <ViewContainer>
                <DataGrid
                    searchV={searchV}
                    selectedRowKeys={selectedRowKeys}
                    setSelectedRowKeys={setSelectedRowKeys}
                    gridProps={{ rowKey: (record: any) => record.atributoID }}
                    columns={columns}
                    searchMethod={setSearchV}
                    menu={menu}
                    loading={loading}
                    dataSource={getAttributesFiltered()}
                    setItemSelected={handleGetAttributeByID}
                    addAction={{ active: true, method: () => { setMode('Add'); setAddUpdateModal(true) } }}
                    deleteAction={{ active: !!attributeUpdate, method: () => setDeleteModal(true) }}
                    updateAction={{ active: !!attributeUpdate, method: openModalUpdate }}
                />
            </ViewContainer>

            <ConfirmationModal
                title={menu.nombre}
                visible={deleteModal}
                setVisible={setDeleteModal}
                question="¿Seguro que deseas eliminar este Atributo?"
                method={handleDeactiveAttribute}
                loading={loadingAction}
            />

            <Modal props={{ style: { top: '-10px' } }} title={<nav className="modal_title"> {mode === 'Add' ? 'Crear' : 'Modificar'}  <Paint color={COLORS.blue_main(1)}>Atributo</Paint></nav>} visible={addUpdateModal} setVisible={setAddUpdateModal} askBeforeClose={true}>
                <AddUpdateAttribute setDataList={setListAttribute} dataList={listAttribute} attribute={attributeUpdate} mode={mode} setAttributeUpdate={setAttributeUpdate} setAddUpdateModal={setAddUpdateModal} listDepartment={listDepartment} loadingDeparment={loadingDeparment} />
            </Modal>
        </MenuLayout>
    )
}


export default Atributos;